import * as React from "react";
import * as ReactDOM from "react-dom";
import styled, { createGlobalStyle } from "styled-components";

import { Icon } from "./Icon";
import { media, mediaMax } from "./common";
import { isNumber } from "lodash";

export enum Position {
  TOP = "top",
  BOTTOM = "bottom",
  RIGHT = "right",
}

const ARROW_SIZE = 10;

export interface CustomCss {
  tooltipWrapper?: {
    width?: string;
  };
  wrapper?: {
    maxWidth?: number;
    left?: number;
    leftMinus?: number;
    top?: number;
  };
  arrow?: {
    left?: number | string;
    top?: string;
    color?: string;
  };
}

interface TooltipProps {
  content?: React.ReactNode;
  children?: React.ReactNode;
  trigger?: "hover" | "click" | "static";
  width?: string;
  className?: string;
  closable?: boolean;
  position?: Position;
  isHidden?: boolean;
  zIndex?: number;
  customCss?: CustomCss;
  hideOnMouseLeave?: boolean;
}

interface TooltipStates {
  visible: boolean;
  position?: Position;
  boundingRect?: DOMRect;
  contentRect?: DOMRect;
  zIndex?: number;
  customCss?: CustomCss;
}

export class Tooltip extends React.Component<TooltipProps, TooltipStates> {
  static defaultProps = {
    trigger: "click",
    closable: true,
  };
  state: TooltipStates = { visible: false, position: Position.TOP };

  private wrapperRef: any;
  private contentRef: any;

  private shouldStopPropogation: boolean = false;
  private triggeredByTouchStart = false;
  private mouseEnteredAt: Date | null = null;
  private bodyContainer: HTMLDivElement = document.createElement("div");
  private mouseOverHandler: number | null = null;
  componentDidMount() {
    document.body.appendChild(this.bodyContainer);
    const { trigger } = this.props;

    if (trigger === "static") {
      this.setState({ visible: true });
    }
  }

  componentDidUpdate(prevProps: TooltipProps, prevState: TooltipStates) {
    const { position, visible } = this.state;
    if (prevState.visible !== visible && visible) {
      const wrapperRect: DOMRect = this.wrapperRef?.getBoundingClientRect();
      const contentRect: DOMRect = this.contentRef?.getBoundingClientRect();
      const scrollTop = window.screenTop;

      if (contentRect && wrapperRect) {
        const { height } = contentRect;
        const { top } = wrapperRect;
        const calculatePosition =
          prevProps.position ||
          (top - scrollTop > height ? Position.TOP : Position.BOTTOM);
        if (calculatePosition !== position || visible !== prevState.visible) {
          this.setState({ position: calculatePosition });
        }
      }
    }
  }

  shouldComponentUpdate(
    nextProps: Readonly<TooltipProps>,
    nextState: Readonly<TooltipStates>
  ): boolean {
    const { visible } = this.state;
    const { isHidden } = this.props;

    const isHiddenUpdate =
      (isHidden !== undefined && !isHidden) || isHidden !== nextProps.isHidden;
    return visible || visible !== nextState.visible || isHiddenUpdate;
  }

  componentWillUnmount() {
    document.body.removeChild(this.bodyContainer);
    this.unBindEvents();
  }

  renderContent() {
    const { content, width, closable, className, zIndex, customCss } =
      this.props;
    const { visible, position = Position.TOP } = this.state;
    const wrapperRect: DOMRect = this.wrapperRef
      ? this.wrapperRef.getBoundingClientRect()
      : undefined;
    const contentRect: DOMRect = this.contentRef
      ? this.contentRef.getBoundingClientRect()
      : undefined;
    return (
      <TooltipContent
        boundingRect={wrapperRect}
        contentRect={contentRect}
        position={position}
        visible={visible}
        className={className}
        ref={(ref) => (this.contentRef = ref)}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        onTouchStart={(event: React.TouchEvent) => event.stopPropagation()}
        width={width}
        zIndex={zIndex}
        customCss={customCss}
      >
        {closable && <Icon type="close" onClick={this.onClick} />}
        {content}
      </TooltipContent>
    );
  }

  renderPortal() {
    const contentNode = this.renderContent();
    return ReactDOM.createPortal(contentNode, this.bodyContainer);
  }

  bindAction() {
    const { trigger } = this.props;
    if (trigger === "hover") {
      return {
        onMouseEnter: this.onMouseover,
        onMouseLeave: this.onMouseleave,
      };
    } else if (trigger === "click") {
      return {
        onClick: this.onClick,
      };
    }
  }
  render() {
    const { children, trigger, isHidden, customCss } = this.props;

    return (
      <>
        <GlobalStyle />
        <TooltipWrapper
          {...this.bindAction()}
          onTouchStart={
            trigger === "click"
              ? (event: React.TouchEvent) => event.stopPropagation()
              : this.onTouchStart
          }
          ref={(ref) => (this.wrapperRef = ref)}
          isHidden={isHidden}
          width={customCss?.tooltipWrapper?.width}
        >
          {children}
          {this.renderPortal()}
        </TooltipWrapper>
      </>
    );
  }

  private onMouseover = () => {
    this.shouldStopPropogation = this.triggeredByTouchStart;
    if (!this.mouseEnteredAt) {
      this.mouseEnteredAt = new Date();
      // @ts-ignore
      this.mouseOverHandler = setTimeout(() => {
        this.setState({ visible: true }, () => {
          this.bindEvents();
        });
      }, 500);
    }
  };

  private onMouseleave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (this.mouseEnteredAt && this.mouseOverHandler) {
      if (this.props.hideOnMouseLeave) {
        this.setState({ visible: false });
      }

      if (new Date().getTime() - this.mouseEnteredAt.getTime() < 500) {
        clearTimeout(this.mouseOverHandler);
      }
    }
    this.mouseEnteredAt = null;
  };

  private onTouchStart = () => {
    this.triggeredByTouchStart = true;
  };

  private windowClickHandler = () => {
    if (!this.shouldStopPropogation) {
      this.unBindEvents();
      this.setState({ visible: false });
    } else {
      this.shouldStopPropogation = false;
      this.triggeredByTouchStart = false;
    }
  };

  private windowResizeHandler = () => {
    this.forceUpdate();
  };

  private onClick = () => {
    this.shouldStopPropogation = true;
    const { visible } = this.state;
    this.setState({ visible: !visible }, () => {
      const { visible } = this.state;
      if (visible) {
        this.bindEvents();
      } else {
        this.unBindEvents();
      }
    });
  };

  private bindEvents() {
    window.addEventListener("click", this.windowClickHandler);
    window.addEventListener("touchstart", this.windowClickHandler);
    window.addEventListener("resize", this.windowResizeHandler);
  }

  private unBindEvents() {
    window.removeEventListener("click", this.windowClickHandler);
    window.removeEventListener("touchstart", this.windowClickHandler);
    window.removeEventListener("resize", this.windowResizeHandler);
  }
}

const GlobalStyle = createGlobalStyle`
  .form-tooltip-popup{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }
`;

const TooltipWrapper = styled.div<{ isHidden?: boolean; width?: string }>`
  display: ${(props) => (props.isHidden ? "none" : "inline-block")};
  position: relative;
  width: ${({ width }) => (width ? width : "auto")};
`;

const calculateTipTop = (
  position: Position,
  buttonRect?: DOMRect,
  tipRect?: DOMRect,
  customCss?: CustomCss
): number => {
  if (!buttonRect || !tipRect) {
    return 0;
  }
  if (customCss?.wrapper?.top) {
    return customCss.wrapper.top;
  }
  const scrollTop =
    document.documentElement.scrollTop || document.body.scrollTop;
  switch (position) {
    case Position.TOP:
      return buttonRect.top - tipRect.height - 15 + scrollTop;
    case Position.BOTTOM:
      return buttonRect.top + buttonRect.height + 15 + scrollTop;
    case Position.RIGHT:
    default:
      return buttonRect.top - 15 + scrollTop;
  }
};
const calculateTipLeft = (
  position: Position,
  buttonRect?: DOMRect,
  tipRect?: DOMRect,
  defaultLeft?: number,
  customCss?: CustomCss
): number => {
  if (!buttonRect || !tipRect || !tipRect.width) {
    return 0;
  }

  if (customCss?.wrapper?.left) {
    return customCss.wrapper.left;
  }

  let left = 0;
  const arrowLeft = buttonRect.left + buttonRect.width / 2 - 8;
  left = buttonRect.left + buttonRect.width / 2 - tipRect.width / 2;
  if (defaultLeft) {
    const minLeft = arrowLeft - tipRect.width + 24;
    if (defaultLeft < minLeft) {
      left = minLeft;
    } else if (arrowLeft < defaultLeft) {
      left = arrowLeft - 16 > 0 ? arrowLeft - 16 : 0;
    } else {
      left = defaultLeft;
    }
  }

  let result = 0;
  switch (position) {
    case Position.TOP:
    case Position.BOTTOM:
      result = left < 15 ? 15 : left;
      break;
    case Position.RIGHT:
    default:
      result = buttonRect.right + 15;
      break;
  }
  if (customCss?.wrapper?.leftMinus) {
    result = result - customCss.wrapper.leftMinus;
  }
  return result;
};

const calculateArrowLeft = (
  position: Position,
  buttonRect?: DOMRect,
  tipRect?: DOMRect,
  defaultLeft?: number,
  customCss?: CustomCss
): string => {
  if (position === Position.RIGHT) {
    return `${-(ARROW_SIZE * 2)}px`;
  }
  if (!buttonRect || !tipRect || !tipRect.width) {
    return "0px";
  }
  if (customCss?.arrow?.left) {
    if (isNumber(customCss.arrow.left)) {
      return `${customCss.arrow.left}px`;
    }
    return customCss.arrow.left;
  }
  const arrowLeft = buttonRect.left + buttonRect.width / 2 - 8;
  const left =
    arrowLeft - calculateTipLeft(position, buttonRect, tipRect, defaultLeft);
  return `${left}px`;
};

const calculateArrowTop = (
  position?: Position,
  buttonRect?: DOMRect,
  tipRect?: DOMRect,
  customCss?: CustomCss
) => {
  if (!buttonRect || !tipRect || !position) {
    return 0;
  }
  if (customCss?.arrow?.top) {
    return customCss.arrow.top;
  }
  switch (position) {
    case Position.RIGHT:
      return `${buttonRect.top - tipRect.top}px`;
    case Position.TOP:
    case Position.BOTTOM:
      return `${-(ARROW_SIZE * 2)}px`;
  }
};
const TooltipContent = styled.div<TooltipStates & { width?: string }>`
  display: ${({ visible }) => (visible ? "block" : "none")};
  width: ${({ width }) => (width ? width : "calc(100vw - 48px)")};
  position: absolute;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  background-color: ${(p) => p.theme.textColor};
  z-index: ${({ zIndex }) => (zIndex ? zIndex : 3)};
  top: ${({ position, boundingRect, contentRect }: TooltipStates) =>
    calculateTipTop(position!, boundingRect, contentRect)}px;

  max-width: ${({ customCss }) =>
    customCss?.wrapper?.maxWidth
      ? `${customCss?.wrapper?.maxWidth}px`
      : "auto"};

  ${mediaMax.md`
    left: ${({
      position,
      boundingRect,
      contentRect,
      customCss,
    }: TooltipStates) =>
      calculateTipLeft(position!, boundingRect, contentRect, 24, customCss)}px;
    &::before,
    &::after {
      left: ${({
        position,
        boundingRect,
        contentRect,
        customCss,
      }: TooltipStates) =>
        calculateArrowLeft(
          position!,
          boundingRect,
          contentRect,
          24,
          customCss
        )};
    }
  `}
  ${media.md`
    left: ${({
      position,
      boundingRect,
      contentRect,
      customCss,
    }: TooltipStates) =>
      calculateTipLeft(
        position!,
        boundingRect,
        contentRect,
        undefined,
        customCss
      )}px;
    &::before,
    &::after {
      left: ${({
        position,
        boundingRect,
        contentRect,
        customCss,
      }: TooltipStates) =>
        calculateArrowLeft(
          position!,
          boundingRect,
          contentRect,
          undefined,
          customCss
        )};
    }
    max-width: ${({ customCss }: TooltipStates) =>
      customCss?.wrapper?.maxWidth
        ? `${customCss?.wrapper?.maxWidth}px`
        : "400px"};
  `};
  ${Icon} {
    position: absolute;
    right: 18px;
    top: 10px;
    color: #979797;
    cursor: pointer;
  }
  &::before,
  &::after {
    position: absolute;
    border-width: ${ARROW_SIZE}px;
    border-style: solid;
    border-color: transparent;
  }
  &::before {
    content: ${({ position, visible }) =>
      visible && position !== Position.TOP ? '""' : "none"};
    top: ${({ position, boundingRect, contentRect, customCss }) =>
      calculateArrowTop(position, boundingRect, contentRect, customCss)};
    ${({ position, theme, customCss }) => {
      const borderColor = customCss?.arrow?.color
        ? customCss?.arrow?.color
        : theme.textColor;

      return position === Position.BOTTOM
        ? `border-bottom-color: ${borderColor}`
        : `border-right-color: ${borderColor}`;
    }};
    z-index: ${({ zIndex }) => (zIndex ? zIndex : 3)};
  }
  &::after {
    content: ${({ position, visible }) =>
      visible && position === Position.TOP ? '""' : "none"};
    bottom: -${ARROW_SIZE * 2}px;
    border-top-color: ${(p) =>
      p.customCss?.arrow?.color
        ? p.customCss?.arrow?.color
        : p.theme.textColor};
    z-index: ${({ zIndex }) => (zIndex ? zIndex : 3)};
  }
`;
